// /trips — screen 1 of trip setup: the organizer's trip list plus "create a trip".
//
// Auto-mounted at /trips by the glob router in src/App.tsx (a default export in
// src/pages/<name>.tsx is the whole registration step).
//
// Reads go through the anon client so RLS is exercised: `membership_select` shows
// you your own rows and `trip_select` shows the trips they point at. Creation is
// the one exception — it is multi-step with no membership row to authorize it yet,
// so it runs in the `trips` edge function with the service role.
import { useCallback, useEffect, useState, type JSX } from 'react'
import { navigate } from '../App'
import { AuthProvider, RequireAuth, useAuth } from '../lib/auth'
import { supabase } from '../lib/supabaseClient'
import type { Role, Trip } from '../lib/types'

const css = `
.spz-trips { max-width: 34rem; margin: 0 auto; padding: 1.25rem 1rem 4rem; display: flex; flex-direction: column; gap: 1.25rem; }
.spz-trips h1 { font-size: 1.6rem; letter-spacing: -0.02em; }
.spz-trips h2 { font-size: 1.05rem; }
.spz-trips .spz-muted { color: var(--muted); font-size: 0.9rem; }
.spz-trips ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.6rem; }
.spz-trips li a { display: flex; flex-direction: column; gap: 0.15rem; min-height: 3.25rem; padding: 0.85rem 1rem; border: 1px solid var(--line); border-radius: 0.75rem; text-decoration: none; color: inherit; }
.spz-trips .spz-trip-name { font-weight: 600; }
.spz-trips section { border: 1px solid var(--line); border-radius: 0.75rem; padding: 1rem; display: flex; flex-direction: column; gap: 0.75rem; }
.spz-trips form { display: flex; flex-direction: column; gap: 0.6rem; }
.spz-trips label { display: flex; flex-direction: column; gap: 0.25rem; font-size: 0.8rem; color: var(--muted); }
.spz-trips .spz-row { display: flex; gap: 0.6rem; }
.spz-trips .spz-row > * { flex: 1 1 0; min-width: 0; }
.spz-trips button { min-height: 2.75rem; padding: 0.6rem 1rem; font-weight: 600; border-radius: 0.5rem; border: 1px solid var(--accent); background: var(--accent); color: var(--ground); }
.spz-trips button:disabled { opacity: 0.6; cursor: default; }
.spz-trips .spz-error { color: #b3261e; font-size: 0.85rem; }
`

/** Every column of Trip — a partial select would make the cast a lie. */
const TRIP_COLUMNS =
  'id, name, year, status, organizer_ids, currency, horizon_start, horizon_end, created_at'

interface TripListItem {
  role: Role
  trip: Trip
}

/**
 * PostgREST embeds an FK target as an object, but supabase-js's untyped client
 * widens it to `object | array | null` depending on how it infers the relationship.
 * Normalize instead of guessing. `noUncheckedIndexedAccess` makes `[0]` possibly
 * undefined, which is exactly the case being handled.
 */
function firstTrip(embedded: Trip | Trip[] | null): Trip | null {
  if (!embedded) return null
  return Array.isArray(embedded) ? (embedded[0] ?? null) : embedded
}

function nextYear(): number {
  return new Date().getFullYear() + 1
}

function TripsInner(): JSX.Element {
  const { user } = useAuth()
  const userId = user?.id ?? null

  const [trips, setTrips] = useState<TripListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const year = nextYear()
  const [form, setForm] = useState({
    name: '',
    year: String(year),
    currency: 'USD',
    horizon_start: `${year}-06-01`,
    horizon_end: `${year}-08-31`,
  })

  const loadTrips = useCallback(async (uid: string) => {
    setLoading(true)
    const { data, error: err } = await supabase
      .from('membership')
      .select(`role, trip:trip_id(${TRIP_COLUMNS})`)
      .eq('user_id', uid)

    if (err) {
      setError(err.message)
      setTrips([])
    } else {
      setError(null)
      const rows = (data ?? []) as unknown as Array<{ role: Role; trip: Trip | Trip[] | null }>
      const items: TripListItem[] = []
      for (const row of rows) {
        const trip = firstTrip(row.trip)
        if (trip) items.push({ role: row.role, trip })
      }
      items.sort((a, b) => b.trip.year - a.trip.year || a.trip.name.localeCompare(b.trip.name))
      setTrips(items)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    if (!userId) return
    void loadTrips(userId)
  }, [userId, loadTrips])

  const [showCreate, setShowCreate] = useState(false)

  async function createTrip(e: React.FormEvent) {
    e.preventDefault()
    const name = form.name.trim()
    if (!name) {
      setError('Trip name is required')
      return
    }
    // ISO dates sort lexicographically, so a string compare is a date compare.
    if (form.horizon_end <= form.horizon_start) {
      setError('The horizon end date must be after the start date')
      return
    }

    setSubmitting(true)
    setError(null)
    // Multi-step insert (trip + organizer membership) with no membership row yet
    // for RLS to lean on, so it runs server-side. invoke() attaches the session JWT.
    const { data, error: err } = await supabase.functions.invoke<{
      trip_id: string
    }>('trips', {
      body: {
        name,
        year: Number(form.year),
        currency: form.currency.trim().toUpperCase(),
        horizon_start: form.horizon_start,
        horizon_end: form.horizon_end,
      },
    })
    setSubmitting(false)

    if (err || !data?.trip_id) {
      setError(err?.message ?? 'Could not create the trip')
      return
    }
    navigate(`/trip-setup?trip=${encodeURIComponent(data.trip_id)}`)
  }

  return (
    <main className="spz-trips">
      <style>{css}</style>
      <h1>Trips</h1>

      {loading ? (
        <p className="spz-muted">Loading&hellip;</p>
      ) : trips.length === 0 ? (
        <p className="spz-muted">You&rsquo;re not on any trips yet. Create one below.</p>
      ) : (
        <ul>
          {trips.map(({ trip, role }) => (
            <li key={trip.id}>
              <a href={`/trip-setup?trip=${encodeURIComponent(trip.id)}`}>
                <span className="spz-trip-name">{trip.name}</span>
                <span className="spz-muted">
                  {trip.year} · {role} · {trip.horizon_start} to {trip.horizon_end}
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="spz-error" role="alert">
          {error}
        </p>
      )}

      {/* Folded away once you are already on a trip. This is a one-trip-a-year family,
          and a relative tapping Create becomes organizer of a phantom trip — which then
          makes every screen ask "which trip?" for everyone. Still one tap for the rare
          real case: next year's. */}
      <section>
        <h2>{trips.length === 0 ? 'Create a trip' : 'Start another trip'}</h2>
        {trips.length > 0 && !showCreate && (
          <>
            <p className="spz-muted">
              You&rsquo;re already on {trips.length === 1 ? 'a trip' : 'trips'} above. Only
              start another if you&rsquo;re planning a genuinely different one.
            </p>
            <button type="button" onClick={() => setShowCreate(true)}>
              Start another trip
            </button>
          </>
        )}
        <form
          onSubmit={(e) => void createTrip(e)}
          hidden={trips.length > 0 && !showCreate}
        >
          <label>
            Name
            <input
              type="text"
              required
              placeholder="Saltz family trip"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <div className="spz-row">
            <label>
              Year
              <input
                type="number"
                required
                min={2000}
                max={2100}
                step={1}
                inputMode="numeric"
                value={form.year}
                onChange={(e) => setForm({ ...form, year: e.target.value })}
              />
            </label>
            <label>
              Currency
              <input
                type="text"
                required
                maxLength={3}
                pattern="[A-Za-z]{3}"
                autoCapitalize="characters"
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
              />
            </label>
          </div>
          <label>
            Availability horizon starts
            <input
              type="date"
              required
              value={form.horizon_start}
              onChange={(e) => setForm({ ...form, horizon_start: e.target.value })}
            />
          </label>
          <label>
            Availability horizon ends
            <input
              type="date"
              required
              min={form.horizon_start}
              value={form.horizon_end}
              onChange={(e) => setForm({ ...form, horizon_end: e.target.value })}
            />
          </label>
          <button type="submit" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create trip'}
          </button>
        </form>
        {(trips.length === 0 || showCreate) && (
          <p className="spz-muted">
            The horizon is the range households mark their availability inside. Whoever
            creates the trip becomes its first organizer.
          </p>
        )}
      </section>
    </main>
  )
}

export default function TripsPage(): JSX.Element {
  // Self-wrapped AuthProvider (nest-safe) so the page works before a root provider
  // is mounted in main.tsx.
  return (
    <AuthProvider>
      <RequireAuth>
        <TripsInner />
      </RequireAuth>
    </AuthProvider>
  )
}
